import React, { useEffect, useRef, useState } from 'react'

import styled from '@emotion/styled'

import { component as arcadeComponentColors, semantic as arcadeSemanticColors } from '@arcade/web'

import { spacing } from '../../theme'
import ArcadeRadioButton from '../Arcade/RadioButton/ArcadeRadioButton'
import { Flex } from '../layout/Flex'
import Text from '../Text/Text'

const SPACING_OFFSET = 2

// The overlay appearance is a fixed dark-glass treatment for camera and media
// surfaces, so these colors intentionally do not change with the host theme.
const overlayColors = {
  backgroundFallback: arcadeSemanticColors.backgroundSubtle.dark,
  background: `color-mix(in srgb, ${arcadeSemanticColors.backgroundSubtle.dark} 52%, transparent)`,
  selectedBackgroundFallback: arcadeComponentColors.segmentedControlButtonBackgroundSelected.light,
  selectedBackground: `color-mix(in srgb, ${arcadeComponentColors.segmentedControlButtonBackgroundSelected.light} 80%, transparent)`,
  text: arcadeComponentColors.segmentedControlButtonText.dark,
  selectedText: arcadeComponentColors.segmentedControlButtonText.light,
}

/** Segemented Control Slider */
const SegmentedControlSlider = styled.div<{
  $count: number
  $index: number
  $overlay: boolean
}>`
  position: absolute;
  top: ${({ theme }) => theme.pxToRem(SPACING_OFFSET)};
  bottom: ${({ theme }) => theme.pxToRem(SPACING_OFFSET)};
  left: calc(
    ${({ theme }) => theme.pxToRem(SPACING_OFFSET)} + ${({ $index }) => $index} *
      (100% - ${({ theme }) => theme.pxToRem(2 * SPACING_OFFSET)}) / ${({ $count }) => $count}
  );
  width: calc((100% - ${({ theme }) => theme.pxToRem(2 * SPACING_OFFSET)}) / ${({ $count }) => $count});
  background-color: ${({ $overlay, theme }) =>
    $overlay
      ? overlayColors.selectedBackgroundFallback
      : theme.arcadeColors.component.segmentedControlButtonBackgroundSelected};
  background-color: ${({ $overlay, theme }) =>
    $overlay
      ? overlayColors.selectedBackground
      : theme.arcadeColors.component.segmentedControlButtonBackgroundSelected};
  border-radius: ${({ theme }) => theme.border.radius.pill};
  transition: left 200ms ease;
`

/** Segmented Control Option */
export interface SegmentedControlOption {
  label: string
  value: string
}

const OptionLabel = styled.label<{ $count: number }>`
  cursor: pointer;
  height: 100%;
  flex: 1;
  max-width: calc(100% / ${({ $count }) => $count});
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};

  ${({ theme }) => theme.selectors.keyboard} &:has(input:focus) {
    outline: ${({ theme }) => theme.pxToRem(2)} solid ${({ theme }) => theme.arcadeColors.semantic.borderProminent};
    border-radius: ${({ theme }) => theme.border.radius.pill};
  }
`

const OptionContainer = styled(Flex)`
  height: 100%;
  align-items: center;
`

const OptionText = styled(Text)<{ $overlay: boolean; $selected: boolean }>`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  width: 100%;
  text-align: center;
  color: ${({ $overlay, $selected }) =>
    $overlay ? ($selected ? overlayColors.selectedText : overlayColors.text) : undefined};
`

interface SegmentedControlInputProps {
  label: string
  name?: string
  value: string
  checked?: boolean
  count: number
  disabled?: boolean
  onChange?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  overlay: boolean
}

const SegmentedControlInput = ({
  label,
  name,
  value,
  count,
  overlay,
  checked,
  ...restProps
}: SegmentedControlInputProps) => {
  const inputId = `${name}-${value}`

  return (
    <OptionLabel $count={count}>
      <OptionContainer flexDirection="row" gap={spacing.s}>
        <ArcadeRadioButton
          {...restProps}
          checked={checked}
          id={inputId}
          name={name}
          value={value}
          isSegmentedControl={true}
        />
        <OptionText $overlay={overlay} $selected={Boolean(checked)} color="textStandard" typography="button">
          {label}
        </OptionText>
      </OptionContainer>
    </OptionLabel>
  )
}

/** Segemented Control component */
const SegmentedControlContainer = styled.fieldset<{ $overlay: boolean }>`
  display: flex;
  flex-direction: row;
  background-color: ${({ $overlay, theme }) =>
    $overlay ? overlayColors.backgroundFallback : theme.arcadeColors.component.segmentedControlBackground};
  background-color: ${({ $overlay, theme }) =>
    $overlay ? overlayColors.background : theme.arcadeColors.component.segmentedControlBackground};
  border-radius: ${({ theme }) => theme.border.radius.pill};
  height: 3.25rem;
  position: relative;
  padding: ${({ theme }) => theme.pxToRem(SPACING_OFFSET)};
  ${({ $overlay }) => $overlay && 'backdrop-filter: blur(16px);'}

  &:disabled {
    opacity: 0.5;
  }
`

const OptionsContainer = styled(Flex)`
  position: relative;
  width: 100%;
`

export type SegmentedControlProps = Omit<
  React.FieldsetHTMLAttributes<HTMLFieldSetElement>,
  'aria-label' | 'children' | 'defaultValue' | 'disabled' | 'onChange'
> & {
  /** Two to five mutually exclusive options. */
  options: SegmentedControlOption[]
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  ariaLabel: string
  name?: string
  appearance?: 'standard' | 'overlay'
  disabled?: boolean
}

export const SegmentedControl = React.forwardRef<HTMLFieldSetElement, SegmentedControlProps>(
  (
    {
      options,
      value,
      defaultValue,
      onChange,
      ariaLabel,
      name,
      appearance = 'standard',
      disabled = false,
      ...fieldSetProps
    },
    ref
  ) => {
    const containerRef = useRef<HTMLFieldSetElement>(null)
    const optionRefs = useRef<(HTMLInputElement | null)[]>([])
    const count = options.length
    const overlay = appearance === 'overlay'

    if (process.env['NODE_ENV'] !== 'production' && (count < 2 || count > 5)) {
      // eslint-disable-next-line no-console -- warns consumers during development about the supported API contract
      console.warn('SegmentedControl expects between 2 and 5 options.')
    }

    const isControlled = value !== undefined
    const initialValue = isControlled ? value : defaultValue || options[0]?.value || ''
    const [internalValue, setInternalValue] = useState(initialValue)
    const currentValue = isControlled ? value : internalValue

    const selectedIndex = options.findIndex(opt => opt.value === currentValue)

    const handleOptionClick = (optionValue: string) => {
      if (disabled) return

      if (!isControlled) {
        setInternalValue(optionValue)
      }
      onChange?.(optionValue)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, currentIndex: number) => {
      if (disabled) return

      const delta = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0
      if (!delta) return

      event.preventDefault()
      const newIndex = Math.min(count - 1, Math.max(0, currentIndex + delta))
      if (newIndex === currentIndex) return

      const newValue = options[newIndex].value

      if (!isControlled) {
        setInternalValue(newValue)
      }
      onChange?.(newValue)

      // Focus the next radio input
      setTimeout(() => {
        const nextInput = optionRefs.current[newIndex]
        if (nextInput) {
          nextInput.focus()
        }
      }, 0)
    }

    // Update refs when options change
    useEffect(() => {
      if (containerRef.current) {
        const radioInputs = containerRef.current.querySelectorAll('input[type="radio"]')
        optionRefs.current = Array.from(radioInputs) as HTMLInputElement[]
      }
    }, [options])

    return (
      <SegmentedControlContainer
        {...fieldSetProps}
        $overlay={overlay}
        ref={ref || containerRef}
        aria-label={ariaLabel}
        disabled={disabled}
        role="radiogroup"
      >
        <SegmentedControlSlider
          $count={count}
          $index={Math.max(selectedIndex, 0)}
          $overlay={overlay}
          data-index={Math.max(selectedIndex, 0)}
          data-testid="segmented-control-slider"
        />
        <OptionsContainer>
          {options.map((option, index) => {
            const isSelected = selectedIndex === index
            return (
              <SegmentedControlInput
                key={option.value}
                name={name}
                value={option.value}
                checked={isSelected}
                count={count}
                disabled={disabled}
                onChange={() => handleOptionClick(option.value)}
                onKeyDown={e => handleKeyDown(e, index)}
                label={option.label}
                overlay={overlay}
              />
            )
          })}
        </OptionsContainer>
      </SegmentedControlContainer>
    )
  }
)

SegmentedControl.displayName = 'SegmentedControl'

export default SegmentedControl
